/**
 * Play OpenAI Speech API SSE stream (`stream_format: sse`, `response_format: pcm`).
 * Events: `speech.audio.delta` with base64 PCM (24 kHz, 16-bit signed LE).
 */

const OPENAI_PCM_SAMPLE_RATE = 24000;

/** Reuse one AudioContext across multiple SSE segments for gapless-ish playback. */
export type PcmPlaybackReuse = {
  ctx: AudioContext;
  nextTimeRef: { current: number };
};

export function normalizeSseBuffer(s: string): string {
  return s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function base64ToUint8Array(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Decode SSE blocks and schedule PCM chunks on an AudioContext (low-latency streaming).
 */
export async function playOpenAiSpeechSsePcm(params: {
  body: ReadableStream<Uint8Array> | null;
  signal?: AbortSignal;
  isCancelled: () => boolean;
  /** Called after AudioContext is created (for suspend/close from outside). */
  onAudioContext?: (ctx: AudioContext) => void;
  /** Append to an existing timeline; avoids closing/reopening the context between sentence chunks. */
  reuse?: PcmPlaybackReuse;
  /** When false, caller must close the context (e.g. after a multi-segment chain). Default: true when not reusing. */
  closeContextAfterPlayback?: boolean;
  /** When not using `reuse`, still record end time for the next segment (streaming chain). */
  timelineOut?: { current: number };
}): Promise<void> {
  const { body, signal, isCancelled, onAudioContext, reuse } = params;
  const closeAfter =
    params.closeContextAfterPlayback !== undefined
      ? params.closeContextAfterPlayback
      : !reuse;
  if (!body) throw new Error('No response body');

  const ctx = reuse?.ctx ?? new AudioContext({ sampleRate: OPENAI_PCM_SAMPLE_RATE });
  onAudioContext?.(ctx);
  if (!reuse) {
    try {
      await ctx.resume();
    } catch (e) {
      try {
        await ctx.close();
      } catch {
        /* */
      }
      throw e;
    }
  }

  let nextTime = reuse ? Math.max(reuse.nextTimeRef.current, ctx.currentTime) : ctx.currentTime;
  let pcmCarry = new Uint8Array(0);

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = '';

  const playPcmBytes = (bytes: Uint8Array) => {
    const merged = new Uint8Array(pcmCarry.length + bytes.length);
    merged.set(pcmCarry, 0);
    merged.set(bytes, pcmCarry.length);
    const evenLen = merged.length - (merged.length % 2);
    pcmCarry = merged.slice(evenLen);
    if (evenLen < 2) return;

    const aligned = merged.subarray(0, evenLen);
    const samples = new Int16Array(aligned.buffer, aligned.byteOffset, evenLen / 2);
    const float32 = new Float32Array(samples.length);
    for (let i = 0; i < samples.length; i++) float32[i] = samples[i] / 32768;

    const audioBuffer = ctx.createBuffer(1, float32.length, OPENAI_PCM_SAMPLE_RATE);
    audioBuffer.getChannelData(0).set(float32);
    const src = ctx.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(ctx.destination);
    const startAt = Math.max(nextTime, ctx.currentTime);
    src.start(startAt);
    nextTime = startAt + audioBuffer.duration;
  };

  const processSseBlock = (block: string) => {
    for (const line of block.split('\n')) {
      const t = line.trim();
      if (!t.startsWith('data:')) continue;
      const raw = t.slice(5).trim();
      if (!raw || raw === '[DONE]') continue;
      try {
        const parsed = JSON.parse(raw) as {
          type?: string;
          audio?: string;
          error?: { message?: string };
        };
        if (parsed.type === 'speech.audio.done') continue;
        if (parsed.error?.message) {
          throw new Error(parsed.error.message);
        }
        if (parsed.type === 'speech.audio.delta' && typeof parsed.audio === 'string') {
          playPcmBytes(base64ToUint8Array(parsed.audio));
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
  };

  try {
    while (true) {
      if (isCancelled() || signal?.aborted) break;
      const { done, value } = await reader.read();
      if (value) sseBuffer += decoder.decode(value, { stream: true });
      if (done) {
        sseBuffer += decoder.decode(new Uint8Array(), { stream: false });
        break;
      }
      sseBuffer = normalizeSseBuffer(sseBuffer);
      let idx: number;
      while ((idx = sseBuffer.indexOf('\n\n')) >= 0) {
        const block = sseBuffer.slice(0, idx);
        sseBuffer = sseBuffer.slice(idx + 2);
        processSseBlock(block);
      }
    }
    sseBuffer = normalizeSseBuffer(sseBuffer);
    let idx: number;
    while ((idx = sseBuffer.indexOf('\n\n')) >= 0) {
      const block = sseBuffer.slice(0, idx);
      sseBuffer = sseBuffer.slice(idx + 2);
      processSseBlock(block);
    }
    if (sseBuffer.trim()) processSseBlock(sseBuffer);
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* */
    }
  }

  /**
   * SSE finishes before the last PCM has played — scheduled buffers extend past `nextTime`.
   * Closing AudioContext immediately would cut off all remaining audio (~1s of early stop).
   */
  if (!isCancelled() && !signal?.aborted) {
    const remainingSec = Math.max(0, nextTime - ctx.currentTime);
    if (remainingSec > 0) {
      const ms = Math.min(remainingSec * 1000 + 150, 600_000);
      await new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        const onAbort = () => {
          clearTimeout(id);
          resolve();
        };
        signal?.addEventListener('abort', onAbort, { once: true });
      });
    }
  }

  if (reuse) {
    reuse.nextTimeRef.current = nextTime;
  } else if (params.timelineOut) {
    params.timelineOut.current = nextTime;
  }

  if (closeAfter) {
    try {
      await ctx.close();
    } catch {
      /* */
    }
  }
}
